import { FunctionComponent, useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CircularProgress from '@mui/material/CircularProgress';
import SingleChoice from '../../ai/inputs/SingleChoice/SingleChoice';
import MultipleChoice from '../../ai/inputs/MultipleChoice/MultipleChoice';
import PainScale from '../../ai/inputs/PainScale/PainScale';
import DateTimePicker from '../../ai/inputs/DateTimePicker/DateTimePicker';
import MedicationEntry from '../../ai/inputs/MedicationEntry/MedicationEntry';
import BodyMapSelector from '../../ai/inputs/BodyMapSelector/BodyMapSelector';
import VitalsInput from '../../ai/inputs/VitalsInput/VitalsInput';
import ConsentCheckboxes from '../../ai/inputs/ConsentCheckboxes/ConsentCheckboxes';
import NumericInput, { units } from '../../ai/inputs/NumericInput/NumericInput';
import HeightInput from '../../ai/inputs/HeightInput/HeightInput';
import TextInput from '../../ai/inputs/TextInput/TextInput';
import { aiFollowupApi, ApiError } from '../../../services/api';
import styles from './AISearchFollowup.module.css';

type QuestionType = 'single-choice' | 'multiple-choice' | 'pain-scale' | 'date'
 | 'time' | 'medication-entry' | 'text' | 'body-map' | 'vitals' | 'consent' 
 | 'numeric' | 'height' | 'weight';

interface Question {
  id: string;
  type: QuestionType;
  question: string;
  helperText?: string;
  required?: boolean;
  options?: Array<{ value: string; label: string; }>;
  maxSelections?: number;
  variant?: string;
  hasNextButton?: boolean;
  unit?: string;
  min?: number;
  max?: number;
  precision?: number;
  index: number;
  totalQuestions: number;
  progress: number;
}

interface FormData {
  [key: string]: any;
}

const AISearchFollowup: FunctionComponent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [formData, setFormData] = useState<FormData>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoingBack, setIsGoingBack] = useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [consentItems, setConsentItems] = useState<any[]>([]);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const loadingMessageTimerRef = useRef<NodeJS.Timeout | null>(null);

  const loadingMessages = [
    "Processing your answer...",
    "Analyzing your response...",
    "Preparing your next question..."
  ];

  const backLoadingMessages = [
    "Going back...",
    "Retrieving previous question...",
    "Almost there..."
  ];

  const query = searchParams.get('q') || '';
  const context = searchParams.get('context') || 'general';

  // Effect to rotate loading messages when submitting or going back
  useEffect(() => {
    if (isSubmitting || isGoingBack) {
      // Reset to first message
      setLoadingMessageIndex(0);
      
      // Set up timer to rotate messages
      let messageIndex = 0;
      const messages = isGoingBack ? backLoadingMessages : loadingMessages;
      
      loadingMessageTimerRef.current = setInterval(() => {
        if (messageIndex < messages.length - 1) {
          messageIndex += 1;
          setLoadingMessageIndex(messageIndex);
        }
        // If at the last message, do nothing (keep showing last message)
      }, 2000); // Change message every 2 seconds
      
      return () => {
        if (loadingMessageTimerRef.current) {
          clearInterval(loadingMessageTimerRef.current);
          loadingMessageTimerRef.current = null;
        }
      };
    } else {
      // Clear timer when not submitting or going back
      if (loadingMessageTimerRef.current) {
        clearInterval(loadingMessageTimerRef.current);
        loadingMessageTimerRef.current = null;
      }
    }
  }, [isSubmitting, isGoingBack]);

  // Initialize session when component mounts
  useEffect(() => {
    const initSession = async () => {
      try {
        const result = await aiFollowupApi.createSession({ query, context });
        
        if (result.success) {
          setSessionId(result.data.sessionId);
          // Start polling for the first question
          pollForQuestion(result.data.sessionId);
        } else {
          console.error('Failed to initialize session:', result.error);
          navigate('/ai-search');
        }
      } catch (error) {
        console.error('Error initializing session:', error);
        if (error instanceof ApiError) {
          console.error('API Error:', error.status, error.message);
        }
        navigate('/ai-search');
      }
    };

    initSession();
  }, [query, context, navigate]);

  // Poll for question
  const pollForQuestion = async (sessionIdParam: string, previousQuestionId?: string) => {
    let pollCount = 0;
    const maxPolls = 60; // Poll for up to 60 seconds
    const pollInterval = 1000; // Poll every second

    const poll = async (): Promise<boolean> => {
      try {
        const result = await aiFollowupApi.getQuestion(sessionIdParam, previousQuestionId);
        
        if (result.success && result.data) {
          if (result.data.completed) {
            // Questionnaire completed, redirect
            navigate(result.data.redirectUrl);
            return true;
          }
          
          setCurrentQuestion(result.data.question);
          
          // If it's a consent question, fetch consent items
          if (result.data.question.type === 'consent') {
            fetchConsentItems();
          }
          
          setIsLoading(false);
          setIsSubmitting(false); // Clear submitting state when new question is ready
          setIsGoingBack(false); // Clear going back state when new question is ready
          return true;
        }
      } catch (error: any) {
        if (error instanceof ApiError && error.status === 202) {
          // Still generating, continue polling
          console.log('Question still generating, will retry...');
          return false;
        }
        console.error('Error polling for question:', error);
        setIsLoading(false);
        setIsSubmitting(false); // Clear submitting state on error
        setIsGoingBack(false); // Clear going back state on error
        navigate('/ai-search');
        return true;
      }
      
      return false;
    };

    // Initial poll
    const success = await poll();
    
    if (!success && pollCount < maxPolls) {
      // Continue polling
      const pollTimer = setInterval(async () => {
        pollCount++;
        const done = await poll();
        
        if (done || pollCount >= maxPolls) {
          clearInterval(pollTimer);
          
          if (pollCount >= maxPolls && !done) {
            console.error('Timed out waiting for question');
            setIsLoading(false);
            setIsSubmitting(false); // Clear submitting state on timeout
            setIsGoingBack(false); // Clear going back state on timeout
            navigate('/ai-search');
          }
        }
      }, pollInterval);
    }
  };

  // Fetch consent items when needed
  const fetchConsentItems = async () => {
    // In a real app, this would fetch from the server
    // For now, we'll use the same consent items as before
    setConsentItems([
      {
        id: 'hipaa',
        label: 'I authorize the sharing of my health information',
        description: 'I understand that my health information will be shared with my selected healthcare providers in accordance with HIPAA regulations.',
        required: true,
        infoLink: {
          text: 'Learn about HIPAA',
          url: 'https://www.hhs.gov/hipaa/for-patients/index.html'
        }
      },
      {
        id: 'telehealth',
        label: 'I consent to telehealth services',
        description: 'I agree to receive healthcare services via video consultation when appropriate and understand the limitations of remote care.',
        required: false
      },
      {
        id: 'communication',
        label: 'I consent to receive appointment reminders',
        description: 'I agree to receive appointment reminders and health information via email, SMS, and phone calls.',
        required: false
      },
      {
        id: 'billing',
        label: 'I acknowledge financial responsibility',
        description: 'I understand that I am financially responsible for any costs not covered by my insurance.',
        required: true
      },
      {
        id: 'research',
        label: 'I consent to anonymous data use for research',
        description: 'I agree that my anonymized health data may be used for medical research and improving healthcare services.',
        required: false,
        infoLink: {
          text: 'Privacy Policy',
          url: '/privacy-policy'
        }
      }
    ]);
  };

  const updateFormData = (questionId: string, value: any) => {
    setFormData(prev => ({ ...prev, [questionId]: value }));
    // Clear error when user provides input
    if (errors[questionId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  const validateCurrentQuestion = (overrideValue?: any) => {
    if (currentQuestion?.required) {
      const value = overrideValue !== undefined ? overrideValue : formData[currentQuestion.id];
      if (!value || (Array.isArray(value) && value.length === 0)) {
        setErrors({ [currentQuestion.id]: 'This field is required' });
        return false;
      }
    }
    return true;
  };

  const submitAnswer = async (answer: any) => {
    console.log('[AISearchFollowup] submitAnswer called:', { sessionId, currentQuestion: currentQuestion?.id, answer });
    
    if (!sessionId || !currentQuestion) {
      console.log('[AISearchFollowup] Missing sessionId or currentQuestion');
      return;
    }

    setIsSubmitting(true);
    try {
      console.log('[AISearchFollowup] Making API call to submit answer');
      const result = await aiFollowupApi.submitAnswer({
        sessionId,
        questionId: currentQuestion.id,
        answer
      });

      console.log('[AISearchFollowup] API response:', result);

      if (result.success) {
        // Clear the form data
        setFormData({});
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Start polling for next question (keep isSubmitting true)
        pollForQuestion(sessionId, currentQuestion.id);
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.status, error.message);
      }
      setIsSubmitting(false);
    }
  };

  const handleNext = (overrideValue?: any) => {
    console.log('[AISearchFollowup] handleNext called:', { 
      overrideValue, 
      currentQuestionId: currentQuestion?.id,
      formData: formData[currentQuestion?.id || '']
    });
    
    if (validateCurrentQuestion(overrideValue)) {
      const answer = overrideValue !== undefined ? overrideValue : formData[currentQuestion!.id];
      console.log('[AISearchFollowup] Submitting answer:', answer);
      submitAnswer(answer);
    } else {
      console.log('[AISearchFollowup] Validation failed');
    }
  };

  const handlePrevious = async () => {
    if (!sessionId || !currentQuestion) return;

    setIsGoingBack(true);
    try {
      const result = await aiFollowupApi.goToPrevious({
        sessionId,
        currentQuestionId: currentQuestion.id
      });

      if (result.success) {
        if (result.data.question) {
          // Question was returned directly
          setCurrentQuestion(result.data.question);
          
          // If it's a consent question, fetch consent items
          if (result.data.question.type === 'consent') {
            fetchConsentItems();
          }
          
          // Clear the form data for the new question
          setFormData({});
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setIsGoingBack(false);
        } else if (result.data.status === 'regenerating') {
          // Need to poll for the question (fallback)
          setFormData({});
          window.scrollTo({ top: 0, behavior: 'smooth' });
          pollForQuestion(sessionId);
        }
      }
    } catch (error) {
      console.error('Error going to previous question:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.status, error.message);
      }
      setIsGoingBack(false);
    }
  };

  // Auto-advance handlers
  const handleSingleChoiceChange = (questionId: string, value: string) => {
    console.log('[AISearchFollowup] handleSingleChoiceChange:', { 
      questionId, 
      value, 
      isAdvancing, 
      hasNextButton: currentQuestion?.hasNextButton 
    });
    
    if (isAdvancing) return;
    
    updateFormData(questionId, value);
    
    if (errors[questionId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
    
    // Auto-advance only if no next button
    if (!currentQuestion?.hasNextButton) {
      console.log('[AISearchFollowup] Auto-advancing (no next button)');
      setIsAdvancing(true);
      setTimeout(() => {
        handleNext(value);
        setIsAdvancing(false);
      }, 300);
    }
  };

  const handleDateTimeChange = (questionId: string, value: string) => {
    if (isAdvancing) return;
    
    updateFormData(questionId, value);
    
    if (errors[questionId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
    
    if (value && value.trim() !== '') {
      setIsAdvancing(true);
      setTimeout(() => {
        handleNext(value);
        setIsAdvancing(false);
      }, 500);
    }
  };

  const handlePainScaleChange = (questionId: string, value: number) => {
    if (isAdvancing) return;
    
    updateFormData(questionId, value);
    
    if (errors[questionId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
    
    setIsAdvancing(true);
    setTimeout(() => {
      handleNext(value);
      setIsAdvancing(false);
    }, 500);
  };

  const renderQuestion = (question: Question | null) => {
    if (!question) return null;
    
    const value = formData[question.id];
    const error = errors[question.id];

    switch (question.type) {
      case 'single-choice':
        return (
          <SingleChoice
            id={question.id}
            label={question.question}
            options={question.options || []}
            value={value || ''}
            onChange={(val) => handleSingleChoiceChange(question.id, val)}
            required={question.required}
            helperText={question.helperText}
            error={error}
            variant={question.variant as any}
            hasNextButton={question.hasNextButton}
            onNext={() => handleNext()}
          />
        );

      case 'multiple-choice':
        return (
          <MultipleChoice
            id={question.id}
            label={question.question}
            options={question.options || []}
            values={value || []}
            onChange={(val) => updateFormData(question.id, val)}
            required={question.required}
            helperText={question.helperText}
            error={error}
            maxSelections={question.maxSelections}
            searchable={question.options && question.options.length > 10}
            hasNextButton={question.hasNextButton}
            onNext={() => handleNext()}
          />
        );

      case 'pain-scale':
        return (
          <PainScale
            id={question.id}
            label={question.question}
            value={value || 0}
            onChange={(val) => handlePainScaleChange(question.id, val)}
            required={question.required}
            helperText={question.helperText}
            error={error}
            variant={question.variant as any}
          />
        );

      case 'date':
      case 'time':
        return (
          <DateTimePicker
            id={question.id}
            label={question.question}
            type={question.type}
            value={value || ''}
            onChange={(val) => handleDateTimeChange(question.id, val)}
            required={question.required}
            helperText={question.helperText}
            error={error}
            max={question.type === 'date' ? new Date().toISOString().split('T')[0] : undefined}
          />
        );

      case 'medication-entry':
        return (
          <MedicationEntry
            id={question.id}
            label={question.question}
            medications={value || []}
            onChange={(val) => updateFormData(question.id, val)}
            required={question.required}
            helperText={question.helperText}
            error={error}
            hasNextButton={question.hasNextButton}
            onNext={() => handleNext()}
          />
        );

      case 'text':
        return (
          <TextInput
            id={question.id}
            label={question.question}
            value={value || ''}
            onChange={(val) => updateFormData(question.id, val)}
            multiline={true}
            rows={4}
            required={question.required}
            helperText={question.helperText}
            error={error}
            placeholder={question.helperText}
            hasNextButton={question.hasNextButton}
            onNext={() => handleNext()}
          />
        );

      case 'body-map':
        return (
          <BodyMapSelector
            label={question.question}
            value={value || []}
            onChange={(areas: string[]) => updateFormData(question.id, areas)}
            required={question.required}
            multiple={true}
            hasNextButton={question.hasNextButton}
            onNext={() => handleNext()}
          />
        );

      case 'vitals':
        return (
          <VitalsInput
            label={question.question}
            value={value || {}}
            onChange={(vitals) => updateFormData(question.id, vitals)}
            required={question.required}
            showNormalRanges={true}
            hasNextButton={question.hasNextButton}
            onNext={() => handleNext()}
          />
        );

      case 'consent':
        return (
          <ConsentCheckboxes
            title={question.question}
            items={consentItems}
            value={value || {}}
            onChange={(items) => updateFormData(question.id, items)}
            showDescription={true}
            hasNextButton={question.hasNextButton}
            onNext={() => handleNext()}
          />
        );

      case 'numeric':
        return (
          <NumericInput
            label={question.question}
            value={value}
            onChange={(val) => updateFormData(question.id, val)}
            unit={question.unit}
            min={question.min}
            max={question.max}
            step={1}
            precision={question.precision || 0}
            required={question.required}
            helperText={question.helperText}
            error={error}
            showRange={true}
            hasNextButton={question.hasNextButton}
            onNext={() => handleNext()}
          />
        );

      case 'height':
        return (
          <HeightInput
            label={question.question}
            value={value || {}}
            onChange={(val) => updateFormData(question.id, val)}
            unit={question.unit as 'imperial' | 'metric' || 'imperial'}
            required={question.required}
            helperText={question.helperText}
            error={error}
            hasNextButton={question.hasNextButton}
            onNext={() => handleNext()}
          />
        );

      default:
        return null;
    }
  };

  if (isLoading && !isSubmitting) {
    return (
      <div className={styles.aiSearchFollowup}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <p>Preparing your assessment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.aiSearchFollowup}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => {
          if (currentQuestion?.index === 0) {
            navigate('/ai-search');
          } else {
            handlePrevious();
          }
        }}>
          <ArrowBackIcon />
        </button>
        <h1 className={styles.headerTitle}>Health Assessment</h1>
        <span className={styles.aiLabel}>
          <AutoAwesomeIcon className={styles.aiIcon} />
          AI Powered
        </span>
      </div>

      {currentQuestion && (
        <>
          <div className={styles.progressContainer}>
            <div className={styles.progressBar}>
              <div 
                className={styles.progressFill} 
                style={{ width: `${currentQuestion.progress}%` }}
              />
            </div>
            <div className={styles.progressText}>
              Question {currentQuestion.index + 1}
            </div>
          </div>

          <div className={styles.content}>
            <div className={styles.questionSection}>
              <div className={`${styles.questionCard} ${isSubmitting || isGoingBack ? styles.submitting : ''}`}>
                {renderQuestion(currentQuestion)}
                {(isSubmitting || isGoingBack) && (
                  <div className={styles.loadingOverlay}>
                    <div className={styles.loadingContent}>
                      <CircularProgress size={40} />
                      <p className={styles.loadingText}>
                        {isGoingBack 
                          ? backLoadingMessages[loadingMessageIndex] 
                          : loadingMessages[loadingMessageIndex]}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {!currentQuestion.hasNextButton && (
              <div className={styles.navigation}>
                <button
                  type="button"
                  className={styles.previousButton}
                  onClick={handlePrevious}
                  disabled={currentQuestion.index === 0 || isSubmitting || isGoingBack}
                >
                  <ArrowBackIcon />
                  Back
                </button>

                <button
                  type="button"
                  className={styles.nextButton}
                  onClick={() => handleNext()}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    'Processing...'
                  ) : currentQuestion.index === currentQuestion.totalQuestions - 1 ? (
                    <>
                      Get Care Plan
                      <ChevronRightIcon />
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRightIcon />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      <div className={styles.disclaimer}>
        <p>Your information is secure and will only be used to provide personalized health recommendations.</p>
      </div>
    </div>
  );
};

export default AISearchFollowup;